import { Component, Input, inject } from '@angular/core';

import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';

import { NutrientGroupDto } from '../../../shared/models/models';
import { BemUtils } from '../../shared/bem-utils';
import { MatCard, MatCardContent } from '@angular/material/card';
import { NotificationEmptyComponent } from '@next/core-lib/components';

@Component({
    selector: 'app-nutrient-advice-tab',
    templateUrl: './nutrient-advice-tab.component.html',
    styleUrls: ['./nutrient-advice-tab.component.scss'],
    imports: [
        MatCard,
        MatCardContent,
        NotificationEmptyComponent,
        TranslatePipe,
    ],
})
export class NutrientAdviceTabComponent {
  private readonly locale = inject(LocaleService);

  @Input() nutrientGroup: NutrientGroupDto | undefined;
  @Input() isOutdated: boolean | undefined;
  @Input() sampleMoment!: 'n1' | 'n2' | 'n3';

  getAdviceText(textKeys: string[], recKgha: number | null = 0): string {
    let finalText = '';
    textKeys.forEach((textKey) => {
      const splittedKey = BemUtils.seperateNutrientAndTextId(textKey);
      const translationKey = `BFP.${splittedKey?.nutrient}.${splittedKey?.textId}`;
      finalText += ` ${this.locale.translate(translationKey, { recKgha: recKgha?.toString() ?? '' })}`;
    });
    return finalText.trim();
  }

  hasValidData(nutrientGroup: NutrientGroupDto): boolean {
    return BemUtils.hasValidData(nutrientGroup);
  }

  shouldShowMgRecommendation(): boolean {
    if (!this.nutrientGroup?.mgAdviceTxt?.length) {
      return false;
    }
    return this.sampleMoment === 'n3' && this.containsAdviceCode(this.nutrientGroup.mgAdviceTxt, 'Mg4');
  }

  shouldShowMnRecommendation(): boolean {
    if (!this.nutrientGroup?.mnAdviceTxt?.length) {
      return false;
    }

    if (this.sampleMoment === 'n1') {
      return this.containsAdviceCode(this.nutrientGroup.mnAdviceTxt, 'Mn1');
    } else if (this.sampleMoment === 'n2') {
      return this.containsAdviceCode(this.nutrientGroup.mnAdviceTxt, 'Mn3');
    } else if (this.sampleMoment === 'n3') {
      return this.containsAdviceCode(this.nutrientGroup.mnAdviceTxt, ['Mn5', 'Mn6', 'Mn7']);
    }

    return false;
  }

  shouldShowBRecommendation(): boolean {
    if (!this.nutrientGroup?.bAdviceTxt?.length) {
      return false;
    }

    if (this.sampleMoment === 'n1' || this.sampleMoment === 'n3') {
      return this.containsAdviceCode(this.nutrientGroup.bAdviceTxt, 'B1');
    }

    return false;
  }

  shouldShowSRecommendation(): boolean {
    if (!this.nutrientGroup?.so3AdviceTxt?.length) {
      return false;
    }

    if (this.sampleMoment === 'n1') {
      return this.containsAdviceCode(this.nutrientGroup.so3AdviceTxt, ['S1', 'S2']);
    }

    return false;
  }

  private containsAdviceCode(adviceTexts: string[], codes: string | string[]): boolean {
    const codeArray = Array.isArray(codes) ? codes : [codes];

    return adviceTexts.some((text) => codeArray.some((code) => text.toLowerCase() === code.toLowerCase()));
  }
}
